import {
  type FC,
  type MouseEvent,
  type MouseEventHandler,
  useEffect,
  useState,
} from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { useHistory } from "react-router";

import { Text, toastError } from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetOwnerDetails,
  useMutationRequest,
} from "~/v2/api/hooks";
import {
  type InvestmentObjective,
  patchAccount,
  patchDetails,
  type RiskTolerance,
} from "~/v2/api/rest/account";
import { OPTIONS_TIERS } from "~/v2/constants";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { usePageTimer } from "../../../entityOnboarding/usePageTimer";
import { Layout } from "../../layout";
import { ProgressionButton } from "../../ProgressionButton";
import { InvestmentExperienceStep } from "../../steps";
import { TierBox } from "../../TierBox";
import {
  isLocalStorageAvailable,
  logAmplitudeEvent,
  storage,
} from "../../util";
import { AccountGoalsInput } from "./AccountGoalsInput";
import { RiskToleranceInput } from "./RiskToleranceInput";
import { type InvestmentExperience, zInvestmentExperience } from "./schema";
import { TimeHorizonInput } from "./TimeHorizonInput";
import { YearsExperienceInputs } from "./YearsExperienceInputs";

export type Tier = "1" | "2" | "3";

const TIER_2_INVESTMENT_OBJECTIVES: Omit<
  InvestmentObjective,
  "generate_income" | "preserve_wealth" | "balance_preserve_wealth_with_growth"
>[] = ["market_speculation", "growth"];

const TIER_2_RISK_TOLERANCES: Omit<RiskTolerance, "conservative">[] = [
  "moderate",
  "significant_risk",
];

export const InvestmentExperiencePage: FC = () => {
  const history = useHistory();

  const { currentAccount } = useAccountContext({ liveOnly: true });
  const { details } = useGetOwnerDetails();

  const pageTimer = usePageTimer("options_investment_xp_page");

  const [tier, setTier] = useState<Tier | null>(null);
  const [isTier2AndUpDisabled, setIsTier2AndUpDisabled] = useState(false);

  const onTierSelected =
    (tier: Tier) => (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setTier(tier);
      storage.setTier(tier);
    };

  const form = useForm<InvestmentExperience>({
    mode: "onChange",
    resolver: zodResolver(zInvestmentExperience),
  });

  const mutation = useMutationRequest(
    ({
      investment_time_horizon,
      risk_tolerance,
      investment_objective,
      investment_experience_with_options,
      investment_experience_with_stocks,
    }: InvestmentExperience) =>
      Promise.all([
        patchDetails(currentAccount?.id ?? "", {
          investment_experience_with_options,
          investment_experience_with_stocks,
        }),
        patchAccount(currentAccount?.id ?? "", {
          risk_tolerance,
          investment_objective,
          investment_time_horizon,
        }),
      ]),
    {
      onSuccess: () => {
        const duration = pageTimer.stop();
        logAmplitudeEvent.pageCompleted(duration);

        history.push(
          tier === "3"
            ? Path.ROUTE_OPTIONS_OPT_IN_SPREADS
            : Path.ROUTE_OPTIONS_OPT_IN_AGREEMENT,
        );
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Error: ${errorMessage}`);
      },
    },
  );

  const onBackClicked: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    pageTimer.stop();
    logAmplitudeEvent.backClicked();
    history.push(Path.ROUTE_OPTIONS_OPT_IN_FINANCIAL_PROFILE);
  };

  const onSubmitInvalid: SubmitErrorHandler<InvestmentExperience> = () => {
    toastError("Error: Invalid Form. Please fill out all required fields");
  };

  const onSubmit: SubmitHandler<InvestmentExperience> = (
    values: InvestmentExperience,
  ) => {
    logAmplitudeEvent.nextClicked();
    const storageTier = storage.getTier();

    if (!storageTier) {
      toastError("Error: Please select a Options Trading Tier");
      return;
    }

    mutation.mutate(values);
  };

  const riskTolerance = form.watch("risk_tolerance");
  const investmentObjective = form.watch("investment_objective");

  useEffect(() => {
    const isTier2AndUpDisabled =
      (investmentObjective &&
        !TIER_2_INVESTMENT_OBJECTIVES.includes(investmentObjective)) ||
      (riskTolerance && !TIER_2_RISK_TOLERANCES.includes(riskTolerance));

    if (isTier2AndUpDisabled && (tier === "2" || tier === "3")) {
      storage.removeTier();
      setTier(null);
    }

    setIsTier2AndUpDisabled(isTier2AndUpDisabled);
  }, [tier, investmentObjective, riskTolerance]);

  useEffect(() => {
    if (currentAccount && details) {
      form.reset({
        investment_experience_with_options:
          details.investment_experience_with_options,
        investment_experience_with_stocks:
          details.investment_experience_with_stocks,
        investment_objective: currentAccount.investment_objective,
        investment_time_horizon: currentAccount.investment_time_horizon,
        risk_tolerance: currentAccount.risk_tolerance,
      } as InvestmentExperience);
    }
  }, [currentAccount, details, form.reset]);

  useEffect(() => {
    if (!isLocalStorageAvailable()) {
      toastError(
        "Error: Local Storage on your browser is disabled. Local storage is required for this application progress. Please enable local storage and try again.",
      );
    }
  }, []);

  return (
    <Layout currentStep={InvestmentExperienceStep}>
      <h2 className="pb-6 font-bold text-xl">Investment Experience</h2>

      <form onSubmit={form.handleSubmit(onSubmit, onSubmitInvalid)}>
        <YearsExperienceInputs form={form} />
        <TimeHorizonInput form={form} />
        <AccountGoalsInput form={form} />
        <RiskToleranceInput form={form} />

        <Text className="max-w-lg pb-2 font-bold leading-5">
          Options Trading Tier
        </Text>
        <Text className="pb-4 text-gray-700">
          Only options trading levels that align with your selected goals and
          risk levels are available.
        </Text>
        <div className="flex flex-col space-y-4 min-[500px]:flex-row min-[500px]:space-x-4 min-[500px]:space-y-0">
          <TierBox
            title="Level 1"
            subTitle="Options Strategies:"
            features={OPTIONS_TIERS[1]}
            isSelected={tier === "1"}
            onClick={onTierSelected("1")}
          />
          <TierBox
            title="Level 2"
            subTitle="Everything in Level 1, plus:"
            features={OPTIONS_TIERS[2]}
            isSelected={tier === "2"}
            onClick={onTierSelected("2")}
            isDisabled={isTier2AndUpDisabled}
          />
          <TierBox
            title="Level 3"
            subTitle="Everything in Level 2, plus:"
            features={OPTIONS_TIERS[3]}
            isSelected={tier === "3"}
            onClick={onTierSelected("3")}
            isDisabled={isTier2AndUpDisabled}
          />
        </div>

        <div className="flex max-w-lg items-center justify-between pt-4">
          <ProgressionButton
            variant="secondary"
            onClick={onBackClicked}
            type="button"
          >
            Back
          </ProgressionButton>
          <ProgressionButton type="submit" disabled={tier === null}>
            Next
          </ProgressionButton>
        </div>
      </form>
    </Layout>
  );
};

export default InvestmentExperiencePage;
